import React, { useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import MapComponent from './components/Map';
import Legend from './components/Legend';
import PrivacyPolicy from './components/PrivacyPolicy';
import AboutPage from './components/AboutPage';
import LocationContent from './components/LocationContent';
import { CENSUS_METRICS, US_STATES } from './constants';
import { CensusMetric, USState } from './types';
import './App.css';

const App: React.FC = () => {
  const [selectedMetric, setSelectedMetric] = useState<CensusMetric>(CENSUS_METRICS[0]);
  const [dataRange, setDataRange] = useState<{ min: number, max: number } | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Parse location to extract slugs
  const pathParts = location.pathname.split('/').filter(Boolean);
  
  let stateSlug: string | null = null;
  let countySlug: string | null = null;
  let metricSlug: string | null = null;

  if (pathParts[0] === 'state') {
    stateSlug = pathParts[1];
    if (pathParts[2] === 'county') {
      countySlug = pathParts[3];
      metricSlug = pathParts[4]; // e.g., /state/texas/county/harris/population
    } else {
      metricSlug = pathParts[2]; // e.g., /state/texas/population
    }
  } else if (pathParts[0] === 'metric') {
    metricSlug = pathParts[1]; // e.g., /metric/population
  }

  const selectedState = stateSlug
    ? US_STATES.find(s => s.name.toLowerCase().replace(/\s+/g, '-') === stateSlug) || null
    : null;

  // Sync metric from URL or fallback to state
  const urlMetric = metricSlug
    ? CENSUS_METRICS.find(m => m.label.toLowerCase().replace(/[^a-z0-9]+/g, '-') === metricSlug || m.id === metricSlug)
    : null;

  // Prioritize URL metric, otherwise use local state
  const activeMetric = urlMetric || selectedMetric;

  // Helper to generate the correct URL based on current selections
  const generateUrl = (st: USState | null, co: string | null, met: CensusMetric | null) => {
    let url = '/';
    if (st) {
       url += `state/${st.name.toLowerCase().replace(/\s+/g, '-')}`;
       if (co) {
         url += `/county/${co}`;
       }
    }
    if (met) {
       const mSlug = met.label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
       if (!st) {
           url += `metric/${mSlug}`;
       } else {
           url += `/${mSlug}`;
       }
    }
    return url;
  };

  const handleStateChange = (state: USState | null) => {
    navigate(generateUrl(state, null, urlMetric ? activeMetric : null));
  };

  const handleStateClick = (slug: string) => {
    const st = US_STATES.find(s => s.name.toLowerCase().replace(/\s+/g, '-') === slug) || null;
    navigate(generateUrl(st, null, urlMetric ? activeMetric : null));
  };
  
  const handleCountyClick = (countyName: string) => {
    const slug = countyName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    navigate(generateUrl(selectedState, slug, urlMetric ? activeMetric : null));
  };

  const handleMetricChange = (metric: CensusMetric) => {
    setSelectedMetric(metric);
    navigate(generateUrl(selectedState, countySlug, metric));
  };

  const openPrivacy = () => navigate('/privacy');
  const openAbout = () => navigate('/about');
  
  const closeModal = () => {
    if (selectedState) {
       navigate(`/state/${selectedState.name.toLowerCase().replace(/\s+/g, '-')}`);
    } else {
       navigate('/');
    }
  };

  return (
    <div className="app-container">
      <Sidebar
        selectedMetric={activeMetric}
        onMetricChange={handleMetricChange}
        selectedState={selectedState}
        onStateChange={handleStateChange}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        onOpenPrivacy={openPrivacy}
        onOpenAbout={openAbout}
      />

      <main className="main-content">
        <button
          className="mobile-toggle-btn"
          onClick={() => setIsSidebarOpen(true)}
          aria-label="Open Map Controls"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <MapComponent
          metric={activeMetric}
          selectedState={selectedState}
          selectedCountySlug={countySlug}
          geoLevel="county"
          onRangeChange={setDataRange}
          onStateClick={handleStateClick}
          onCountyClick={handleCountyClick}
        />

        <Legend
          metric={activeMetric}
          min={dataRange?.min}
          max={dataRange?.max}
        />

        <Routes>
          <Route path="/*" element={<LocationContent selectedState={selectedState} countySlug={countySlug} metric={activeMetric} />} />
        </Routes>
      </main>

      <Routes>
        <Route path="/privacy" element={<PrivacyPolicy onClose={closeModal} />} />
        <Route path="/about" element={<AboutPage onClose={closeModal} />} />
      </Routes>
    </div>
  );
};

export default App;